import { useMemo, useState, type UIEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { BottomSheet } from '@/components/BottomSheet'
import { DocumentPickerPanel } from '@/components/DocumentPickerPanel'
import { ConversationHistorySheet } from '@/pages/chat/components/ConversationHistorySheet'
import { ChatMessagesPanel } from '@/pages/chat/components/ChatMessagesPanel'
import { ChatInputBar } from '@/pages/chat/components/ChatInputBar'
import { CitationSheet } from '@/pages/chat/components/CitationSheet'
import { ChatSettingsSheet } from '@/pages/chat/components/ChatSettingsSheet'
import { ChatFloatingActions } from '@/pages/chat/components/ChatFloatingActions'
import { ChatContentHeader } from '@/pages/chat/components/ChatContentHeader'
import { resolveConversationTitle } from '@/lib/conversations'
import { formatSpaceLabel } from '@/pages/chat/chat-utils'
import {
  CHAT_INPUT_MAX_HEIGHT,
  CHAT_INPUT_MIN_HEIGHT,
  useChatPageController,
} from '@/pages/chat/useChatPageController'

export default function ChatPage() {
  const navigate = useNavigate()
  const [showChatSettings, setShowChatSettings] = useState(false)
  const [dimFloatingActions, setDimFloatingActions] = useState(false)

  const {
    settings,
    setTheme,
    setWebSearchMode,
    spaces,
    conversations,
    messages,
    error,
    conversationId,
    selectedSpaceId,
    contextMode,
    selectedDocIds,
    showConvList,
    showDocsPicker,
    documents,
    docsLoading,
    docsUploading,
    chatUploadJobId,
    chatUploadInfo,
    acceptAttr,
    maxUploadMb,
    selectedCitation,
    chatMessagesRef,
    chatInputRef,
    uploadInputRef,
    input,
    isLoading,
    conversationTreeRows,
    setShowConvList,
    setShowDocsPicker,
    setSelectedCitation,
    setInput,
    handleKeyDown,
    toggleBranchCollapse,
    getTreePrefix,
    startNewConversation,
    applySpaceSelection,
    refreshSpaces,
    renameConversation,
    switchConversation,
    handleBranchConversation,
    handleDeleteConversation,
    applyContextMode,
    loadDocuments,
    toggleSelectedDocument,
    uploadDocumentsToChat,
    sendMessage,
    plan,
  } = useChatPageController()

  // Платные фичи (анализ документов, веб-поиск) — только премиум. Иначе бейджи «Pro».
  const isPremium = plan?.plan === 'premium' || Boolean(plan?.features?.doc_analysis)

  const spaceItems = useMemo(() => (
    Array.isArray(spaces?.items) ? spaces.items : []
  ), [spaces?.items])

  const currentConversation = useMemo(() => {
    const items = Array.isArray(conversations?.items) ? conversations.items : []
    if (!conversationId) return undefined
    return items.find((conversation) => conversation.id === conversationId)
  }, [conversations?.items, conversationId])

  const firstUserMessage = useMemo(() => {
    const value = messages.find((message) => message.role === 'user' && message.content.trim())?.content?.trim()
    return String(value || '')
  }, [messages])

  const hasChatActivity = useMemo(() => {
    const hasUserMessage = messages.some((message) => message.role === 'user' && message.content.trim())
    if (hasUserMessage) return true
    return Number(currentConversation?.message_count || 0) > 0
  }, [messages, currentConversation?.message_count])

  const chatTitle = useMemo(() => {
    if (!hasChatActivity) return 'ИИ ассистент по законам'
    return resolveConversationTitle(
      currentConversation?.title,
      currentConversation?.last_message,
      firstUserMessage || 'Новый диалог',
    )
  }, [currentConversation?.last_message, currentConversation?.title, firstUserMessage, hasChatActivity])

  const chatSubtitle = useMemo(() => {
    if (!hasChatActivity) return 'Личный юридический помощник'
    const modeLabel = contextMode === 'space_shared' ? 'Общий контекст' : 'Личный контекст'
    return `${formatSpaceLabel(spaceItems, selectedSpaceId)} • ${modeLabel}`
  }, [contextMode, hasChatActivity, selectedSpaceId, spaceItems])

  const activeConversation = useMemo(() => {
    if (currentConversation) return currentConversation
    if (!conversationId) return undefined
    const fallbackTitle = firstUserMessage || 'Новый диалог'
    return {
      id: conversationId,
      title: fallbackTitle,
      last_message: null,
      message_count: Number(messages.length),
      space_id: selectedSpaceId,
      context_mode: contextMode,
      selected_doc_ids: selectedDocIds,
    }
  }, [contextMode, conversationId, currentConversation, firstUserMessage, messages.length, selectedDocIds, selectedSpaceId])

  const closePanels = () => {
    if (showConvList) setShowConvList(false)
    if (showDocsPicker) setShowDocsPicker(false)
    if (showChatSettings) setShowChatSettings(false)
  }

  const handleMessagesScroll = (event: UIEvent<HTMLDivElement>) => {
    setDimFloatingActions(event.currentTarget.scrollTop > 12)
  }

  return (
    <div className="chat-page flex flex-col h-full overflow-hidden relative">
      <ChatFloatingActions
        dimmed={dimFloatingActions}
        onOpenSettings={() => {
          setShowConvList(false)
          setShowDocsPicker(false)
          setShowChatSettings(true)
          void refreshSpaces()
        }}
        onNewChat={() => {
          setShowConvList(false)
          setShowDocsPicker(false)
          setShowChatSettings(false)
          void startNewConversation()
        }}
      />

      <ConversationHistorySheet
        open={showConvList}
        rows={conversationTreeRows}
        conversationId={conversationId}
        onClose={() => setShowConvList(false)}
        onSwitchConversation={(conversation) => {
          switchConversation(conversation.id, conversation.selected_doc_ids, {
            spaceId: conversation.space_id,
            contextMode: conversation.context_mode,
          })
        }}
        onToggleBranchCollapse={toggleBranchCollapse}
        getTreePrefix={getTreePrefix}
        onBranchConversation={(conversation) => {
          void handleBranchConversation(conversation)
        }}
        onDeleteConversation={(conversation) => {
          void handleDeleteConversation(conversation)
        }}
        getSpaceLabel={(spaceId) => {
          const resolved = formatSpaceLabel(spaceItems, String(spaceId || '').trim() || undefined)
          return resolved === 'Проект' ? 'Без проекта' : resolved
        }}
      />

      <ChatSettingsSheet
        open={showChatSettings}
        spaces={spaceItems}
        selectedSpaceId={selectedSpaceId}
        contextMode={contextMode}
        onClose={() => setShowChatSettings(false)}
        onSelectSpace={(spaceId) => {
          void applySpaceSelection(spaceId)
        }}
        onApplyContextMode={(nextMode) => {
          void applyContextMode(nextMode)
        }}
        currentChatTitle={
          activeConversation
            ? resolveConversationTitle(activeConversation.title, activeConversation.last_message, 'Новый диалог')
            : 'Чат еще не создан'
        }
        canManageCurrentChat={Boolean(activeConversation?.id)}
        onRenameCurrentChat={() => {
          if (!activeConversation?.id) return
          const currentTitle = resolveConversationTitle(
            activeConversation.title,
            activeConversation.last_message,
            'Новый диалог',
          )
          const nextTitle = window.prompt('Новое название чата', currentTitle)
          if (!nextTitle || !nextTitle.trim()) return
          void renameConversation(activeConversation.id, nextTitle)
        }}
        onDeleteCurrentChat={() => {
          if (!activeConversation?.id) return
          setShowChatSettings(false)
          void handleDeleteConversation(activeConversation)
        }}
        theme={settings.theme === 'dark' ? 'dark' : 'light'}
        onSetTheme={(theme) => {
          setTheme(theme)
        }}
        onOpenProjects={() => {
          setShowChatSettings(false)
          navigate('/projects')
        }}
      />

      <BottomSheet
        open={showDocsPicker}
        onClose={() => setShowDocsPicker(false)}
        title="Документы чата"
        description="Выберите документы, которые будут использоваться в текущем чате."
        maxBodyHeight="72vh"
      >
        <DocumentPickerPanel
          inSheet
          selectedDocIds={selectedDocIds}
          documents={documents}
          docsLoading={docsLoading}
          docsUploading={docsUploading}
          docsError={null}
          acceptAttr={acceptAttr}
          maxUploadMb={maxUploadMb}
          uploadButtonTitle={`Загрузить документ в чат (${acceptAttr}, до ${maxUploadMb} MB)`}
          onUploadClick={() => uploadInputRef.current?.click()}
          onRefresh={() => {
            void loadDocuments()
          }}
          onToggleDocument={toggleSelectedDocument}
          onClose={() => setShowDocsPicker(false)}
        />
      </BottomSheet>

      <ChatMessagesPanel
        messages={messages}
        error={error}
        chatMessagesRef={chatMessagesRef}
        onSelectCitation={setSelectedCitation}
        onContainerClick={closePanels}
        onScroll={handleMessagesScroll}
        topSlot={(
          <ChatContentHeader
            title={chatTitle}
            subtitle={chatSubtitle}
            isIntro={!hasChatActivity}
          />
        )}
      />

      <ChatInputBar
        chatUploadInfo={chatUploadInfo}
        chatUploadJobId={chatUploadJobId}
        uploadInputRef={uploadInputRef}
        acceptAttr={acceptAttr}
        docsUploading={docsUploading}
        onUploadFiles={(files) => {
          void uploadDocumentsToChat(files)
        }}
        onToggleDocsPicker={() => {
          setShowConvList(false)
          setShowChatSettings(false)
          setShowDocsPicker((prev) => !prev)
        }}
        showDocsPicker={showDocsPicker}
        selectedDocCount={selectedDocIds.length}
        webSearchMode={settings.webSearchMode}
        onWebSearchModeChange={setWebSearchMode}
        isPremium={isPremium}
        canDocs={isPremium || (plan?.free_doc_analysis_remaining ?? 0) > 0}
        canWeb={isPremium || (plan?.free_web_search_remaining ?? 0) > 0}
        onUpgrade={() => navigate('/settings')}
        isLoading={isLoading}
        input={input}
        onInputChange={setInput}
        onKeyDown={handleKeyDown}
        chatInputRef={chatInputRef}
        inputMinHeight={CHAT_INPUT_MIN_HEIGHT}
        inputMaxHeight={CHAT_INPUT_MAX_HEIGHT}
        onSend={() => {
          void sendMessage()
        }}
      />

      <CitationSheet
        citation={selectedCitation}
        onClose={() => setSelectedCitation(null)}
      />
    </div>
  )
}
